import { Injectable } from '@angular/core';
import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import { NumericValidationCriteria } from '../models/numeric-validation-criteria.model';
import { NumberFieldType } from '../enum/number-field-type.enum';
import BigNumber from 'bignumber.js';

export interface BooleanFn {
  (): boolean;
}

/**
 * 統一的自定義欄位驗證服務。
 */
@Injectable({
  providedIn: 'root'
})
export class ValidationService {

  constructor() { }

  /**
   * 條件式欄位驗證。
   *
   * @param predicate 符合什麼條件
   * @param validator 要做哪些驗證
   * @param errorNamespace 自定義的錯誤名稱，非必要
   * @returns ValidatorFn
   */
  conditionalValidator(predicate: BooleanFn,
    validator: ValidatorFn,
    errorNamespace?: string): ValidatorFn {

    return (formControl: AbstractControl): ValidationErrors | null => {
      if (!formControl.parent) {
        return null;
      }

      let error = null;
      if (predicate()) {
        error = validator(formControl);
      }

      if (errorNamespace && error) {
        const customError: { [key: string]: any } = {};
        customError[errorNamespace] = error;
        error = customError;
      }
      return error;
    }
  }

  /**
   * 驗證數字欄位的格式是否正確。
   * NumberFieldType.INTEGER: 整數，只能是任意數字，最多 15 位。
   * NumberFieldType.AMOUNT: 常用於描述總值，而不涉及每個單位的價值。小數位數依幣別 precision 顯示，整數最多 10 位。
   * NumberFieldType.PRICE: 單位物品或服務的價值。小數位數最多 6 位，整數最多 10 位。
   * NumberFieldType.PROFIT_PRICE: 小數最多 10 位，整數最多 6 位。
   * NumberFieldType.EXCHANGE_RATE: 小數最多 13 位，整數最多 3 位。
   *
   * @param numericValidationCriteria
   * @returns 如果數字欄位格式錯誤，回傳的 error key = numberPattern
   */
  numberFieldValidator(numericValidationCriteria: (() => NumericValidationCriteria)): ValidatorFn {
    return (formControl: AbstractControl): ValidationErrors | null => {
      if (!formControl.parent) {
        return null;
      }

      const criteria: NumericValidationCriteria = numericValidationCriteria();
      const regexpPattern = '^' + this.getRegexpPattern(criteria) + '$';

      let error = null;
      if (formControl.value) {
        error = !new RegExp(regexpPattern).test(new BigNumber(formControl.value).toString());
      }

      if (error) {
        console.log('regexpPattern = ' + regexpPattern);
        console.log('formControl.value = ' + formControl.value);

        return { numberPattern: true };
      }

      // 驗證結果符合需求，回傳 null
      return null;
    }
  }

  /**
   * 根據數字欄位的類型回傳對應的格式正規表示式 pattern
   *
   * @param criteria
   * @returns 正規表示式 pattern
   */
  private getRegexpPattern(criteria: NumericValidationCriteria): string {
    const numberFieldType = criteria.numberFieldType;
    const integerDigits = criteria.integerDigits;
    const fractionDigits = criteria.fractionDigits;

    let regexpPattern = '-?';
    switch (numberFieldType) {
      case NumberFieldType.INTEGER:
        // 整數，只能是任意數字，最多 15 位。
        // ex: '^-?\\d{1,15}$';
        regexpPattern += '\\d{1,15}';
        break;
      case NumberFieldType.AMOUNT:
        // 常用於描述總值，而不涉及每個單位的價值。
        // 小數位數依幣別 precision 顯示，整數最多 10 位。
        // ex: '^(-?\\d{1,10})(?:\\.\\d{1,2})?$';
        regexpPattern = '(' + regexpPattern + '\\d{1,10})';
        if (fractionDigits) {
          regexpPattern += '(?:\\.\\d{1,' + fractionDigits + '})?';
        }
        break;
      case NumberFieldType.PRICE:
        // 單位物品或服務的價值。
        // 小數位數最多 6 位，整數最多 10 位。
        // ex: '^(-?\\d{1,10})(?:\\.\\d{1,6})?$'
        regexpPattern = '(' + regexpPattern + '\\d{1,10})(?:\\.\\d{1,6})?';
        break;
      case NumberFieldType.PROFIT_PRICE:
        // 小數最多 10 位，整數最多 6 位。
        // ex: '^(-?\\d{1,6})(?:\\.\\d{1,10})?$'
        regexpPattern = '(' + regexpPattern + '\\d{1,6})(?:\\.\\d{1,10})?';
        break;
      case NumberFieldType.EXCHANGE_RATE:
        // 小數最多 13 位，整數最多 3 位。
        // ex: '^(-?\\d{1,3})(?:\\.\\d{1,14})?$'
        regexpPattern = '(' + regexpPattern + '\\d{1,3})(?:\\.\\d{1,13})?';
        break;
      case NumberFieldType.CUSTOMER:
        regexpPattern = '(' + regexpPattern + '\\d{1,' + integerDigits + '})';
        if (fractionDigits) {
          regexpPattern += '(?:\\.\\d{1,' + fractionDigits + '})?';
        }
        break;
      default:
        regexpPattern = '';
        break;
    }

    return regexpPattern;
  }

  /**
   * 驗證截止日期不可早於起始日期
   *
   * @param startDateFormControlName 起始日期的 FormControlName
   * @param endDateFormControlName 截止日期的 FormControlName
   * @returns 如果有截止日期早於起始日期，回傳的 error key = dateRange
   */
  dateRangeValidator(startDateFormControlName: string, endDateFormControlName: string): ValidatorFn {
    return (formControl: AbstractControl): ValidationErrors | null => {
      if (!formControl.parent) {
        return null;
      }

      const startDateControl = formControl.parent.get(startDateFormControlName);
      const endDateControl = formControl.parent.get(endDateFormControlName);

      let error = null;

      if (startDateControl?.value && endDateControl?.value) {
        error = new Date(endDateControl?.value) < new Date(startDateControl?.value);
      }

      if (error) {
        startDateControl?.setErrors({ dateRange: true });
        startDateControl?.markAsDirty();
        endDateControl?.setErrors({ dateRange: true });
        endDateControl?.markAsDirty();
        return { dateRange: true };
      }

      if (startDateControl?.hasError('dateRange')) {
        startDateControl?.setErrors({ ...startDateControl?.errors, dateRange: null });
        // 可能還有其他的欄位驗證需要重新驗證
        startDateControl?.updateValueAndValidity();
      }
      if (endDateControl?.hasError('dateRange')) {
        endDateControl?.setErrors({ ...endDateControl?.errors, dateRange: null });
        // 可能還有其他的欄位驗證需要重新驗證
        endDateControl?.updateValueAndValidity();
      }
      // 驗證結果符合需求，回傳 null
      return null;
    }
  }

  /**
   * 驗證一個值是否大於指定的最大值。
   *
   * @param maximum 最大值，要比較的參照值
   * @param valueToValidate 要驗證的值，用於與最大值進行比較
   */
  validateMaxValue(maximum: string | number): ValidatorFn {
    return (formControl: AbstractControl): ValidationErrors | null => {
      if (!formControl.parent) {
        return null;
      }

      // 如果是 string 代表是要從 FormGroup 取，反之就是數字，可直接拿來比較
      const maximumNumber = typeof maximum === 'string' ?
        formControl.parent.get(maximum)?.value :
        maximum;

      // 要被驗證的數字，直接從 Form Control 取
      const valueToValidateNumber = formControl.value;

      if (maximumNumber !== null && valueToValidateNumber && Math.abs(maximumNumber) < Math.abs(valueToValidateNumber)) {
        return { amountGreaterThan: true };
      }

      return null;
    }
  }

  private hasValidLength(value: any): boolean {
    // non-strict comparison is intentional, to check for both `null` and `undefined` values
    return value != null && typeof value.length === 'number';
  }

  private isEnglishOrHalfWidth(char: string): boolean {
    const englishAndHalfWidthRegex = /[A-Za-z0-9!@#$%^&*()_+[\]{}|;:'",.<>?=-\\ ~`]/;
    return englishAndHalfWidthRegex.test(char);
  }

  maxByteLengthValidator(maxByteLength: number): ValidatorFn {
    return (control: AbstractControl): ValidationErrors| null => {
      if (!this.hasValidLength(control.value)) {
        return null;
      }

      const value = control.value as string;
      let totalByteLength = 0;

      for (let i = 0; i < value.length; i++) {
        const char = value.charAt(i);
        if (this.isEnglishOrHalfWidth(char)) {
          totalByteLength += 1; // English or half-width symbol
        } else {
          totalByteLength += 3; // Other characters
        }
      }

      return totalByteLength > maxByteLength
        ? { 'maxByte': { 'maxBytes': maxByteLength, 'actualBytes': totalByteLength } }
        : null;
    };
  }
}
